import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { withZod } from '@remix-validated-form/with-zod'
import { observer } from 'mobx-react-lite'
import { ReactElement, useEffect, useState } from 'react'
import { z } from 'zod'
import FormInput from 'app/components/form/FormInput'
import { SmartForm } from 'app/components/form/SmartForm'
import { ListVisibilityField } from 'app/components/lists/ListVisibilityField'
import { FilterType } from 'app/components/shared/filters/FiltersContainer'
import { FilterObject } from 'app/filter.utils'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import type { CustomPageType, CustomPages } from 'app/types/entities'
import type { ListVisibility } from 'app/types/list-visibility'
import { closeModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useCustomPageStore, useListStore } from 'app/stores/StoreContext'
import { dispatchCustomPageCreated, setLastCreatedPage, addPendingPageId } from 'lib/customPageEvents'
import { debug } from 'lib/logging'
import { RECORD_TYPES } from 'lib/recordTypes'
import { LIST_ENTITY_TYPE, savedPageOfList } from 'lib/savedLists'

const newListSchema = withZod(
  z.object({
    name: z
      .string()
      .min(1, { message: `Page name is required` })
      .max(255, { message: `Name must be less than 255 characters` })
      .transform((value) => value.replace(/\//g, ` `)),
  }),
)
/**
 * @Props { filters: FilterType[]; join: string; onSuccess: function } - filters are the filters to be applied to the new page, join is `and`/`or` operation to apply to the filters.
 * filters are an array of objects with the following shape: { attribute: string; operator: string; value: string }. If any of the values is missing, the filter will be ignored. OnSuccess is a callback fuction
 * @description This component is used to create a new page with the provided filters and join. When a page is created onSuccess is called to perform any action needed.
 * @returns ReactElement
 */
// observer: reads a feature toggle, which resolves async.
export const CreateNewPage = observer(function CreateNewPage({
  filters,
  join,
  onSuccess,
  onCancel,
}: {
  filters?: FilterType[]
  join?: `and` | `or`
  onSuccess: () => void
  onCancel: () => void
}): ReactElement {
  const customPageStore = useCustomPageStore()
  const listStore = useListStore()
  const savesToList = useFeatureToggle(`grouped-navigation`)
  const [visibility, setVisibility] = useState<ListVisibility>(`shared`)
  const navigation = useNavigate()
  const [appliedFilters, setAppliedFilters] = useState<FilterType[]>([])
  const [searchParams] = useSearchParams()
  const location = useLocation()

  useEffect(() => {
    if (!filters) {
      notifyError(`It is not possible to save your page. No filters were provided`)
      return
    }
    let newPageFilters = filters.filter((filter) => filter.attribute && filter.operator && filter.value)
    if (newPageFilters.length === 0) {
      notifyError(`It was not possible to save your page. No filters were provided`)
      return
    } else {
      setAppliedFilters(newPageFilters)
    }
  }, [filters])

  const saveList = async (pageType: CustomPages, name: string, filterObjects: Partial<FilterObject>[]) => {
    const result = await listStore.create({
      name,
      join,
      filters: JSON.stringify(filterObjects),
      visibility,
      entityType: LIST_ENTITY_TYPE[pageType],
    })
    const list = (result.data as { createList?: { id: string; name: string } } | undefined)?.createList
    if (!list?.id) {
      notifyError(`There was an error creating your list: ${name}. ${result.error?.message ?? ``}`)
      closeModal()
      return
    }
    notifySuccess(`List saved successfully`)
    await listStore.fetchList(undefined, { requestPolicy: `network-only` })
    if (pageType !== `contacts`)
      dispatchCustomPageCreated(savedPageOfList({ ...list, join, filters: filterObjects as never }, pageType))
    const wId = searchParams.get(`wId`)
    navigation(
      pageType === `contacts`
        ? `/lists/${list.id}${wId ? `?wId=${wId}` : ``}`
        : `${location.pathname}?tab=${list.id}${wId ? `&wId=${wId}` : ``}`,
    )
    onSuccess()
  }

  const handleSubmit = async (formData?: { name: string }) => {
    if (!join || !formData) {
      notifyError(`Select how to join the filters`)
      return
    }
    debug(`customPages`, `new page data`, formData)
    let filterObjects = appliedFilters?.map<Partial<FilterObject>>((filter) => ({
      attribute: filter.attribute!,
      operator: filter.operator!,
      value: filter.value,
    }))
    debug(`customPages`, `filterObjects`, filterObjects)
    // Derive type from pathname (e.g. /contacts -> contacts)
    const page = location.pathname.replace(/^\//, ``).split(`/`)[0]
    const recordType = Object.values(RECORD_TYPES).find((type) => type.basePath === `/${page}`)
    const pageType = recordType?.customPageType
    if (savesToList && pageType) {
      await saveList(pageType, formData.name, filterObjects)
      return
    }
    const result = await customPageStore.create({
      name: formData.name,
      page,
      join,
      filters: JSON.stringify(filterObjects),
    })
    const customPage = (result.data as any)?.createCustomPage as (CustomPageType & { error: string }) | undefined
    if (customPage && customPage.id) {
      debug(`customPages`, `resultData`, customPage)
      notifySuccess(`Page saved successfully`)
      addPendingPageId(customPage.id!)
      dispatchCustomPageCreated(customPage)
      setLastCreatedPage(customPage)
      navigation(
        `${location.pathname}?tab=${customPage?.id}&${searchParams.has(`wId`) ? `wId=${searchParams.get(`wId`)}` : ``}`,
      )
      onSuccess()
    } else {
      debug(`customPages`, `error`, customPage)
      notifyError(`There was an error creating your page: ${formData.name}. ${customPage?.error}`)
      closeModal()
    }
  }

  debug(`customPages`, `page`, location.pathname)
  return (
    <SmartForm<{ name: string }>
      id="create-new-list"
      className="w-screen px-7 md:w-[680px]"
      validator={newListSchema}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      preventSubmit
    >
      <FormInput name="name" label="Name" cols={6} required />
      {savesToList && (
        <div className="col-span-6">
          <ListVisibilityField label="Who can see it" value={visibility} onChange={setVisibility} />
        </div>
      )}
    </SmartForm>
  )
})
